import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ScrollPositionService {
  private scrollPositions = new Map<string, number>();
  private clickedItems = new Map<string, string>(); // Store clicked item IDs
  private lastRoute: string = '';
  private shouldRestore = false;

  /** True between NavigationEnd and the first restorePosition() call — components
   *  must NOT scroll to top while this is set. */
  get isScrollRestoring(): boolean {
    return this.shouldRestore;
  }

  constructor(private router: Router) {
    // Track navigation to determine if we should restore scroll position
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Check if we're returning from a detail page to a list page
        this.shouldRestore = this.isReturnNavigation(this.lastRoute, event.url);
        this.lastRoute = event.url;
      });
  }

  /**
   * Check if navigation is returning from detail to list page
   */
  private isReturnNavigation(fromRoute: string, toRoute: string): boolean {
    // Check if we're coming from an offer detail page to any offer list page (including categories)
    if (/^\/oferta\/.+\/\d+$/.test(fromRoute) && /^\/oferty(\/.*)?$/.test(toRoute)) {
      return true;
    }
    
    // Check if we're coming from a partner detail page to any partner list page (including categories)
    if (/^\/partner\/.+\/\d+$/.test(fromRoute) && /^\/partnerzy(\/.*)?$/.test(toRoute)) {
      return true;
    }
    
    // Define other detail page patterns and their corresponding list pages
    const detailToListMap = [
      { detailPattern: /^\/wiadomosci\/.+\/\d+$/, listRoute: '/wiadomosci' }
    ];

    // Check if we're coming from a detail page and going to its list page
    return detailToListMap.some(mapping => 
      mapping.detailPattern.test(fromRoute) && toRoute === mapping.listRoute
    );
  }

  /**
   * Navigate to a route while saving current scroll position and clicked item
   */
  navigateWithScrollSave(route: string[], currentRoute: string, clickedItemId?: string): void {
    this.saveCurrentPosition(currentRoute);
    if (clickedItemId) {
      this.clickedItems.set(currentRoute, clickedItemId);
    }
    this.router.navigate(route);
  }

  /**
   * Save current scroll position for a route
   */
  private saveCurrentPosition(route: string): void {
    const scrollContainers = document.getElementsByClassName('app-content');
    let scrollY = 0;
    
    if (scrollContainers.length > 0) {
      scrollY = (scrollContainers[0] as HTMLElement).scrollTop;
    } else {
      scrollY = window.scrollY || document.documentElement.scrollTop;
    }
    
    this.scrollPositions.set(route, scrollY);
  }

  /**
   * Restore scroll position for a route (only if returning from detail page)
   */
  restorePosition(route: string): void {
    // Only restore if we're returning from a detail page
    if (!this.shouldRestore) {
      return;
    }

    // Check if we have a specific clicked item to scroll to
    const clickedItemId = this.clickedItems.get(route);
    if (clickedItemId) {
      // Try to scroll to the specific item first
      this.scrollToItem(clickedItemId);
    } else {
      // Fallback to saved scroll position
      const position = this.scrollPositions.get(route);
      
      if (position && position > 0) {
        setTimeout(() => {
          const scrollContainers = document.getElementsByClassName('app-content');
          
          if (scrollContainers.length > 0) {
            (scrollContainers[0] as HTMLElement).scrollTo({
              top: position,
              behavior: 'instant'
            });
          } else {
            window.scrollTo({
              top: position,
              behavior: 'instant'
            });
          }
        }, 100);
      }
    }
    
    // Reset the flag after restoration
    this.shouldRestore = false;
  }

  /**
   * Scroll to a specific item by ID
   */
  private scrollToItem(itemId: string): void {
    // Try multipl attempts with increasing delays
    const attemptScroll = (attempt: number = 1, maxAttempts: number = 5) => {
      setTimeout(() => {
        // Try different selectors for finding the item
        const selectors = [
          `[data-offer-id="${itemId}"]`,
          `[data-item-id="${itemId}"]`,
          `.offer-item:nth-child(${itemId})`,
          `#offer-${itemId}`,
          `#item-${itemId}`
        ];

        let element: HTMLElement | null = null;
        
        for (const selector of selectors) {
          element = document.querySelector(selector) as HTMLElement;
          if (element) break;
        }

        if (element) {
         element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        } else if (attempt < maxAttempts) {
          attemptScroll(attempt + 1, maxAttempts);
        } else {
        }
      }, attempt * 200); // Increasing delay: 200ms, 400ms, 600ms, etc.
    };
    
    attemptScroll();
  }

  /**
   * Clear saved position for a route
   */
  clearPosition(route: string): void {
    this.scrollPositions.delete(route);
    this.clickedItems.delete(route);
  }
}